/** Re using moment vs toLocaleDateString() in the code below....
 *  Moment seems better for unit testing because it will give the same results in the test runner as it
 *  does in the browser.
 * 
 *  toLocaleDateString() on the other hand, gives inconsistent results and needs to be mocked out in
 *  some cases for unit tests. So it is used only where needed.
 */
// TODO Just use i18n's dateFormatter
import moment from 'moment';

export const getYearAdjustedDate = ({ axisDate=moment(), date }) => {
  axisDate = moment(axisDate);
  const thisYear = moment().format('YYYY');
  date = moment(date.replace(/YYYY/, thisYear));
  return (axisDate.isSameOrBefore(date) ? date : date.add(1, 'y'));
};

/**
   * format date
   * @param date in ISO8601 DateTime format
   * @param params optional object of options
   * @return date as short month name, day of month, year. ex Apr 1, 2015
   */
export const formatDate = (date, { locale = 'en' } = {}) => {
  moment.locale(locale);
  return moment(date).format('ll');
};

export const formatDateToLocale = (date, locale) => {
  moment.locale(locale);
  return moment(date).format('ll');
};

/**
   * format date
   * @param date in unix timestamp format
   * @return date as short month name, day of month, year. ex Apr 1, 2015
   */
export const formatDateFromUnix = (date) => {
  return moment.unix(date).format('ll');
};

export const convertDateFromUnixToIso = (date) => {
  return moment.unix(date).toISOString();
};

/* Used so that toLocaleDateString() can be mocked to match browser behavior in test runner environement. */
const toLocaleDateString = (date, locale, options) => date.toLocaleDateString(locale, options);

/**
 * Format date with hyphenation. Should not be used for any display text.
 * @param date in unix timestamp format
 * @return short date as month-day-year
 */
export const formatDateHyphenated = (incomingDate = (new Date())) => {
  // TODO: If incomingDate is a string like 'August 26, 2014' this triggers a
  // deprecation warning.  Forcing it into Date() before moment() causes an
  // activitiesParser test to fail (?!).  Fix
  return moment(incomingDate).format('MM-DD-YY');
};

/**
 * Format date for a mosaic v2 call
 * @param date javascript data object
 * @return short date as year-month-day
 */
export const formatDateForMosaic = (date) => encodeURIComponent(date.toISOString());

/**
 * format date with localization, but forcing use of hyphens as separators. 
 * @param date in unix timestamp format
 * @param locale Locale string.
 * @return short date as month-day-year or potentially in a different order depending on locale.
 */
export const formatDateHyphenatedByLocale = (incomingDate = (new Date()), locale) => {
  const dateOpts = { month: '2-digit', year: '2-digit', day: '2-digit' };
  const dateString = toLocaleDateString(new Date(incomingDate), locale, dateOpts).trim();

  // Replace any non-numeric sequences of chars with hyphens.
  let hyphenated = '';
  let inNonNumeric = true;
  for (let i = 0; i < dateString.length; ++i) {
    const char = dateString.charAt(i);
    const isNumeric = (char >= '0' && char <= '9');
    if (isNumeric) {
      hyphenated += char;
      inNonNumeric = false;
    } else {
      if (!inNonNumeric) { hyphenated += '-'; }
      inNonNumeric = true;
    }
  }

  return hyphenated;
};

/**
   * format date
   * @param date in UTC format
   * @return date in unix timestamp format
   */
export const formatDateToUnix = (date) => {
  return moment(date).format('x');
};

/**
 * format date
 * @param year in YYYY format
 * @return date in ISO8601 DateTime format
 */
export const formatYearToISO8601 = (year) => {
  return moment(year, 'YYYY').toISOString();
};

/**
 * format date
 * @param date in string format
 * @return date in ISO8601 DateTime format
 */
export const formatDateToISO8601 = (date) => {
  return moment(date).toISOString();
};

/* returns 4-digit calendar year for a date.  Used when we aren't using 'seasons',
 * That is, either for non-crop based things, or legacy data
 */
export const formatDateToCalendarYear = (incomingDate = (new Date()).toISOString()) => {
  const date = moment(incomingDate);
  const year = date.format('YYYY');
  return year;
};

export const formatDateToMoment = (incomingDate = (new Date())) => {
  return moment(incomingDate);
};

export const dateIsSameOrAfter = ({ anchorDate = (new Date()), comparisonDate }) => {
  if (!comparisonDate) { return false; }
  anchorDate = (new Date(anchorDate)).toISOString();
  comparisonDate = (new Date(comparisonDate)).toISOString();

  return moment(comparisonDate).isSameOrAfter(moment(anchorDate));
};
